import client from "./client";
import { USE_MOCK_API } from "./config";
import { toast } from "react-toastify";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";
import {
  mockSearchPatients,
  mockSavePatient,
} from "../data/mockData";
import { normalizeBedTypes, normalizeBeds } from "../utils/bedNormalization";
import { buildSearchFilter } from "../utils/patientSearch";

import { buildIpdAdmissionPayload } from "../utils/buildSubmitPayload";

function buildPhotoUrl(photo) {
  if (!photo) return "";
  if (String(photo).startsWith("data:")) return photo;
  return `data:image/jpeg;base64,${photo}`;
}

function normalizeGender(gender) {
  const g = String(gender || "").trim();
  if (g === "M" || g === "Male") return "MALE";
  if (g === "F" || g === "Female") return "FEMALE";
  if (g === "O" || g === "Others" || g === "OTHER") return "OTHER";
  if (g.toUpperCase() === "MALE") return "MALE";
  if (g.toUpperCase() === "FEMALE") return "FEMALE";
  return g ? g.toUpperCase() : "";
}

function normalizeDepartmentList(raw) {
  const list = raw?.department_list || (Array.isArray(raw) ? raw : []);
  const options = list.map((dep) => {
    if (typeof dep === "string") {
      return { departmentName: String(dep).trim(), departmentId: "" };
    }
    const departmentName = dep?.department || dep?.name || dep?.department_name || "";
    const departmentId =
      dep?.department_id ||
      dep?.departmentId ||
      dep?.departmentid ||
      dep?.department_code ||
      dep?.department_unique_id ||
      dep?.dept_id ||
      dep?.id ||
      "";
    return {
      departmentName: String(departmentName || "").trim(),
      departmentId: String(departmentId || "").trim(),
    };
  }).filter((d) => d.departmentName);

  options.push({ departmentName: "Other/External", departmentId: "" });

  return {
    options,
    names: options.map((d) => d.departmentName),
  };
}

export async function fetchMedicalDepartments() {
  const data = await client.get("get_all_medical_departments/", {}, { silent: true });
  return normalizeDepartmentList(data);
}

export async function fetchDoctorsByDepartment(department) {
  if (!department || department === "Other/External") return [];
  const data = await client.post(
    "get_all_doctors_based_on_department/",
    { department, assigned_doctors_only: false },
    { silent: true }
  );
  const list = data?.response_list || [];
  return list.map((doctor) => ({
    doctor_id: doctor.doctor_id,
    name: doctor.name,
    specialty: doctor.specialty || department,
  }));
}

export function mapReferralToPatient(referral) {
  const contacts = [{ channel: "PHONE", type: "PRIMARY", value: referral.phone_no || "" }];
  if (referral.email) {
    contacts.push({ channel: "EMAIL", type: "PRIMARY", value: referral.email });
  }

  const line1 = referral.locality || referral.address_line || referral.address || "";
  const hasAddress = line1 || referral.address_city || referral.city
    || referral.address_state || referral.state;

  const mapped = {
    name: referral.name || "",
    gender: normalizeGender(referral.gender),
    dob: referral.dob || "",
    uhid: referral.patient_id || referral.id || "",
    abhaAddress: referral.abha_id || "",
    abhaNumber: referral.abha_no && referral.abha_no !== "NA" ? referral.abha_no : "",
    primaryMobile: referral.phone_no || "",
    photoUrl: buildPhotoUrl(referral.photo),
    contacts,
    encounter: {},
  };

  if (hasAddress) {
    mapped.addresses = [{
      type: "PERMANENT",
      line1,
      city: referral.address_city || referral.city || "",
      district: referral.address_district || referral.district || "",
      state: referral.address_state || referral.state || "",
      country: "India",
      pincode: referral.address_pincode || referral.pincode || "",
    }];
  }

  return mapped;
}

/**
 * Fetches OPD referred patients from ipd_queue/ (same endpoint as RegisterPatientForIpd.js).
 * No mock fallback — errors propagate to the caller.
 */
export async function fetchOpdReferrals() {
  const data = await client.get("ipd_queue/", {}, { silent: true });
  return Array.isArray(data) ? data : (data?.results || data?.referrals || []);
}

export async function searchPatients(criteria) {
  const filterCriteria = buildSearchFilter(criteria);
  if (!filterCriteria) {
    throw new Error("Invalid search type");
  }

  // if (USE_MOCK_API) {
  //   return mockSearchPatients(filterCriteria);
  // }

  const data = await client.post("get_filtered_patient_data/", filterCriteria, { silent: true });
  const details = data?.patient_details;
  if (!details) return [];
  return Array.isArray(details) ? details : [details];
}

export async function savePatientRegistration(patient) {
  // if (USE_MOCK_API) {
  //   return mockSavePatient(patient);
  // }
  const isUpdate = Boolean(patient.uhid);
  const endpoint = isUpdate ? "patient-details-update/" : "register_patient/";
  const data = await client.post(endpoint, patient);
  return { ...patient, uhid: data?.uhid || patient.uhid };
}

/**
 * Submit patient registration / update — mirrors RegisterPatientForIpd.js submit flow
 * (posts to ipd_admission/ with the legacy-compatible payload).
 */
export async function submitIpdPatientRegistration(patient, context = {}) {
  const payload = buildIpdAdmissionPayload(patient, context);

  if (!payload.assigned_doctor_id) {
    throw new Error("Please select assigned doctor before submit.");
  }

  // if (USE_MOCK_API) {
  //   await new Promise((r) => setTimeout(r, 800));
  //   return {
  //     status: "Success",
  //     message: "IPD Admission created successfully!",
  //     admission_id: `ADM-${Date.now()}`,
  //     patient_id: payload.patient_id || `PAT-${Date.now()}`,
  //     payload,
  //   };
  // }
  // console.log("ipd_admission payload →", payload);
  const body = await client.postFull("ipd_admission/", payload);
  const nested = body?.data && typeof body.data === "object" ? body.data : {};
  const merged = { ...nested, ...body };

  if (merged.status && merged.status !== "Success") {
    throw new Error(merged.message || "IPD admission failed.");
  }

  return {
    status: merged.status || "Success",
    message: merged.message || "",
    patient_id: merged.patient_id ?? merged.patientId ?? null,
    uhid: merged.uhid ?? merged.UHID ?? null,
    admission_id: merged.admission_id ?? merged.admissionId ?? null,
  };
}

export async function fetchPatientAdmissionData(patientId, { silent = false } = {}) {
  const normalizedPatientId = String(patientId || "").trim();
  if (!normalizedPatientId) {
    throw new Error("Patient ID is required.");
  }

  try {
    const body = await client.postFull(
      "get_patient_admission_data/",
      { patient_id: normalizedPatientId },
      { silent: true }
    );
    if (!body) {
      throw new Error("Failed to load patient admission data.");
    }

    const admissionData = body.data || body;
    return {
      patient_id: body.patient_id ?? admissionData.patient_id ?? normalizedPatientId,
      uhid: body.uhid ?? admissionData.uhid ?? null,
      admission_id: body.admission_id ?? null,
      admission_status: body.admission_status ?? null,
      data: admissionData,
    };
  } catch (err) {
    const msg = getApiErrorMessage(err);
    if (!silent) toast.error(msg);
    throw new Error(msg);
  }
}

export async function fetchPayersByFinanceClass(financialClass) {
  const normalizedClass = String(financialClass || "").trim();
  if (!normalizedClass) return [];

  const data = await client.get(
    "ipd_admission/payers_by_finance_class/",
    { financial_class: normalizedClass },
    { silent: true }
  );

  const payers = data?.payers;
  return Array.isArray(payers) ? payers : [];
}

export async function fetchReferralDoctors() {
  const data = await client.get(
    "ipd_admission/referal_doctors/",
    {},
    { silent: true }
  );
  const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
  return list.map((doctor) => ({
    id: doctor.uuid || String(doctor.id || ""),
    name: doctor.name || "",
    phone: doctor.phone_no || "",
  }));
}

export async function addReferralDoctorToMaster({ name, phoneNo }) {
  const payload = {
    name: String(name || "").trim(),
    phone_no: String(phoneNo || "").trim(),
  };
  const raw = await client.post(
    "ipd_admission/referal_doctors/",
    payload,
    { silent: true }
  );
  const doctor = raw?.uuid || raw?.id ? raw : (raw?.data || raw);
  return {
    id: doctor?.uuid || String(doctor?.id || ""),
    name: doctor?.name || payload.name,
    phone: doctor?.phone_no || payload.phone_no,
    message: raw?.message || "",
  };
}

// Wards (bed types) with facility-wide availability. This used to require a
// department and return only that department's wards; beds carry no department
// now, so every active ward is offered and the counts are the true ones.
export async function fetchIpdBedTypes() {
  const data = await client.get("ipd_bedtype/", {}, { silent: true });
  return normalizeBedTypes(data);
}

export async function fetchIpdBeds(bedTypeCode) {
  const normalizedBedTypeCode = String(bedTypeCode || "").trim();
  if (!normalizedBedTypeCode) return [];

  const data = await client.get(
    "ipd_bed/",
    { bed_type_code: normalizedBedTypeCode },
    { silent: true }
  );
  return normalizeBeds(data);
}

export async function createIpdAdmission(admissionPayload) {
  if (USE_MOCK_API) {
    await new Promise((r) => setTimeout(r, 600));
    return { admission_id: `ADM-${Date.now()}`, ...admissionPayload };
  }
  return client.post("ipd_admission/", admissionPayload);
}

/** GET /ipd_admission/?status=Allocated — list currently allocated IPD patients. */
export async function getAllocatedAdmissions() {
  const data = await client.get('ipd_admission/', { status: 'Allocated' }, { silent: true });
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.ipd_admissions)) return data.ipd_admissions;
  if (Array.isArray(data?.admission_list)) return data.admission_list;
  return [];
}
